/**
 * Custom hook for managing user profile data and operations
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import {
  useInvalidateUserProfile,
  useMembershipPreferences,
  useSetMembershipPreferences,
  useSetUserProfileData,
  useUserProfileData,
} from '@/hooks/useUserProfileData';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import { DEFAULT_NOTIFICATION_CHANNELS } from '../../lib/profile/constants';
import type {
  NotificationChannel,
  NotificationChannels,
  NotificationType,
  ProfileFormData,
} from '../../lib/profile/types';

const SAVE_PREFERENCE_ERROR = "We couldn't save your preference. Try again.";

/**
 * Custom hook for boolean state that auto-resets to false after a delay
 */
function useAutoResetBoolean(
  resetDelay = 3000
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [value, setValue] = useState(false);

  useEffect(() => {
    if (value) {
      const timer = setTimeout(() => setValue(false), resetDelay);
      return () => clearTimeout(timer);
    }
  }, [value, resetDelay]);

  return [value, setValue];
}

interface UseProfileOptions {
  orgId?: number | null;
  userId?: number | null;
}

export function useProfile(options: UseProfileOptions = {}) {
  const { orgId = null, userId = null } = options;
  // For syncing the membership time-format preference into the org-context
  // cache so every open surface re-renders with the new format immediately.
  const { updateOrgFields, currentOrg } = useOrg();

  // Use TanStack Query for profile data (automatic deduplication)
  const {
    data: profileData,
    isLoading: loading,
    error: queryError,
    isSuccess: profileIsSuccess,
    dataUpdatedAt: profileDataUpdatedAt,
  } = useUserProfileData();

  // True only once a profile fetch has successfully completed against the
  // network. Two things make Boolean(profileData) insufficient on its own:
  // the query hydrates from a localStorage initialData snapshot stamped with
  // dataUpdatedAt === 0 (present but never confirmed live), and a failed
  // refetch flips isSuccess false while leaving the last data in place. Both
  // dataUpdatedAt > 0 and isSuccess are therefore required — consumers that
  // must not trust the optimistic formData defaults until the real account
  // setting is known should gate on this rather than on profileData alone.
  const profileConfirmed = profileIsSuccess && profileDataUpdatedAt > 0;
  const invalidateProfile = useInvalidateUserProfile();
  const setProfileData = useSetUserProfileData();

  // Use TanStack Query for membership preferences
  const {
    data: membershipData,
    isLoading: membershipLoading,
    error: membershipQueryError,
  } = useMembershipPreferences(orgId, userId);
  const setMembershipData = useSetMembershipPreferences(orgId, userId);

  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: '',
    last_name: '',
    timezone: '',
    email_footer_enabled: true,
    email_signature_enabled: true,
    // Off by default: requires the user to grant the gmail.compose OAuth scope.
    gmail_compose_drafts_enabled: false,
    show_proposal_success_state: true,
    notify_on_mention: true,
    notify_on_owned_objects: true,
    // Off by default: agents stay pipeline-only unless the user opts in.
    non_pipeline_actions: false,
    // Off by default: notes require a calendar meeting unless the user opts in.
    capture_notes_without_calendar_meeting: false,
    agent_guidance: '',
    // Off by default: the digest email is opt-in (mirrors OrgMembership.digest_cadence).
    digest_cadence: 'none' as const,
    notification_channels: {},
    notification_social_account_id: null,
    booking_calendar_social_account_id: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useAutoResetBoolean(3000);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [emailFooterSaving, setEmailFooterSaving] = useState(false);
  const [emailSignatureSaving, setEmailSignatureSaving] = useState(false);
  const [gmailComposeDraftsSaving, setGmailComposeDraftsSaving] =
    useState(false);
  const [successConfirmationSaving, setSuccessConfirmationSaving] =
    useState(false);
  const [notifyMentionSaving, setNotifyMentionSaving] = useState(false);
  const [notifyOwnedSaving, setNotifyOwnedSaving] = useState(false);
  const [nonPipelineActionsSaving, setNonPipelineActionsSaving] =
    useState(false);
  const [nonPipelineActionsSaveSuccess, setNonPipelineActionsSaveSuccess] =
    useAutoResetBoolean(3000);
  // Operation-scoped error so a failure elsewhere (profile save, agent
  // guidance, a different toggle) never surfaces under the Proposal scope
  // section, and vice versa.
  const [nonPipelineActionsError, setNonPipelineActionsError] = useState<
    string | null
  >(null);
  const [
    captureNotesWithoutCalendarMeetingSaving,
    setCaptureNotesWithoutCalendarMeetingSaving,
  ] = useState(false);
  const [
    captureNotesWithoutCalendarMeetingSaveSuccess,
    setCaptureNotesWithoutCalendarMeetingSaveSuccess,
  ] = useAutoResetBoolean(3000);
  // Operation-scoped error so a failure elsewhere never surfaces under the
  // capture-notes-without-calendar-meeting toggle, and vice versa.
  const [
    captureNotesWithoutCalendarMeetingError,
    setCaptureNotesWithoutCalendarMeetingError,
  ] = useState<string | null>(null);
  const [agentGuidanceSaving, setAgentGuidanceSaving] = useState(false);
  const [digestCadenceSaving, setDigestCadenceSaving] = useState(false);
  const digestCadenceRequestRef = useRef(0);
  const [notificationEmailSaving, setNotificationEmailSaving] = useState(false);
  const [notificationEmailError, setNotificationEmailError] = useState<
    string | null
  >(null);
  const notificationEmailRequestRef = useRef(0);
  const [bookingCalendarSaving, setBookingCalendarSaving] = useState(false);
  const [bookingCalendarSaveSuccess, setBookingCalendarSaveSuccess] =
    useAutoResetBoolean(3000);
  const [bookingCalendarError, setBookingCalendarError] = useState<
    string | null
  >(null);
  const bookingCalendarRequestRef = useRef(0);
  const [timeFormatSaving, setTimeFormatSaving] = useState(false);
  const timeFormatRequestRef = useRef(0);
  const [notificationChannelsSaving, setNotificationChannelsSaving] =
    useState(false);
  const notificationChannelRefs = useRef<Record<string, number>>({});
  // Ref that tracks the current notification_channels state so setNotificationChannel
  // can read the latest value synchronously without adding formData to its dep array.
  const notificationChannelsRef = useRef<NotificationChannels>({});
  notificationChannelsRef.current = formData.notification_channels;
  const [membershipId, setMembershipId] = useState<number | null>(null);
  const [membershipLoadError, setMembershipLoadError] = useState<string | null>(
    null
  );

  // Auto-clear uploadSuccess (different type - string | null)
  useEffect(() => {
    if (uploadSuccess) {
      const timer = setTimeout(() => setUploadSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [uploadSuccess]);

  // Sync form data with profile data
  useEffect(() => {
    if (profileData?.user) {
      setFormData(prev => ({
        ...prev,
        first_name: profileData.user.first_name || '',
        last_name: profileData.user.last_name || '',
        // The read path resolves unset → "UTC"; the settings form should show
        // "not set" (empty) until the user actually picks one.
        timezone: profileData.user.timezone_is_set
          ? profileData.user.timezone
          : '',
        email_footer_enabled: profileData.user.email_footer_enabled ?? true,
        email_signature_enabled:
          profileData.user.email_signature_enabled ?? true,
        gmail_compose_drafts_enabled:
          profileData.user.gmail_compose_drafts_enabled ?? false,
        show_proposal_success_state:
          profileData.user.show_proposal_success_state ?? true,
      }));
    }
  }, [profileData]);

  // Sync membership data to state when query data changes
  useEffect(() => {
    if (membershipData) {
      setMembershipId(membershipData.id);
      setFormData(prev => ({
        ...prev,
        notify_on_mention: membershipData.notify_on_mention ?? true,
        notify_on_owned_objects: membershipData.notify_on_owned_objects ?? true,
        non_pipeline_actions: membershipData.non_pipeline_actions ?? false,
        capture_notes_without_calendar_meeting:
          membershipData.capture_notes_without_calendar_meeting ?? false,
        agent_guidance: membershipData.agent_guidance ?? '',
        digest_cadence: membershipData.digest_cadence ?? 'none',
        notification_channels: membershipData.notification_channels ?? {},
        notification_social_account_id:
          membershipData.notification_social_account_id ?? null,
        booking_calendar_social_account_id:
          membershipData.booking_calendar_social_account_id ?? null,
      }));
    } else if (orgId && userId) {
      // No membership data while the query is enabled (both ids present). This
      // covers two cases, and both must clear any stale id: (1) a newly-switched
      // org whose query is still loading — the query is keyed by (orgId, userId)
      // with no keepPreviousData, so React Query returns undefined during the
      // switch window; without clearing here, membershipId would retain the
      // previous org's id and a save could PATCH the wrong org's membership; and
      // (2) the query resolved with no membership (user not in this org). A
      // same-org background refetch keeps membershipData defined, so it stays in
      // the branch above and the id is preserved. The (orgId && userId) guard
      // preserves the last known id when the query is disabled (one id null).
      // Repopulated when the new membership data arrives.
      setMembershipId(null);
    }
  }, [membershipData, membershipLoading, orgId, userId]);

  // Sync query error to membershipLoadError state
  useEffect(() => {
    if (membershipQueryError) {
      setMembershipLoadError(
        "We couldn't load your notification preferences. Try again."
      );
    } else {
      setMembershipLoadError(null);
    }
  }, [membershipQueryError]);

  // Invalidate in-flight digest PATCH responses when the org/membership
  // context changes. Bumping the ref means any pending response handler
  // will see requestId !== current and skip user-visible state updates
  // that would otherwise apply to an org the user is no longer viewing.
  // Also reset the saving spinner and any stale "Saved" success indicator
  // so neither persists into a different org's view.
  useEffect(() => {
    digestCadenceRequestRef.current++;
    setDigestCadenceSaving(false);
  }, [orgId, membershipId]);

  // Same context-reset pattern as digest cadence above, for both account
  // pickers: bump the request refs so any in-flight PATCH response is
  // ignored, and clear saving/success/error so neither persists into a
  // different org's view.
  useEffect(() => {
    notificationEmailRequestRef.current++;
    setNotificationEmailSaving(false);
    setNotificationEmailError(null);
    bookingCalendarRequestRef.current++;
    setBookingCalendarSaving(false);
    setBookingCalendarSaveSuccess(false);
    setBookingCalendarError(null);
  }, [orgId, membershipId, setBookingCalendarSaveSuccess]);

  useEffect(() => {
    Object.keys(notificationChannelRefs.current).forEach(key => {
      notificationChannelRefs.current[key]++;
    });
    setNotificationChannelsSaving(false);
  }, [orgId, membershipId]);

  // Reset the Proposal scope (non_pipeline_actions) operation state on context
  // change so a prior org's "Saving"/"Saved" indicator or scoped error never
  // persists into a different org's view — matching the digest and
  // notification-channel resets above.
  useEffect(() => {
    setNonPipelineActionsSaving(false);
    setNonPipelineActionsSaveSuccess(false);
    setNonPipelineActionsError(null);
  }, [orgId, membershipId, setNonPipelineActionsSaveSuccess]);

  // Reset the capture-notes toggle operation state on context change so a
  // prior org's "Saving"/"Saved" indicator or scoped error never persists
  // into a different org's view — matching the non_pipeline_actions reset above.
  useEffect(() => {
    setCaptureNotesWithoutCalendarMeetingSaving(false);
    setCaptureNotesWithoutCalendarMeetingSaveSuccess(false);
    setCaptureNotesWithoutCalendarMeetingError(null);
  }, [orgId, membershipId, setCaptureNotesWithoutCalendarMeetingSaveSuccess]);

  // Wrapper to invalidate and refetch profile data
  const loadProfileData = useCallback(async () => {
    setError(null);
    try {
      await invalidateProfile();
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError("We couldn't load your profile. Try again.");
    }
  }, [invalidateProfile]);

  const updateFormData = useCallback((updates: Partial<ProfileFormData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const saveProfile = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);

      const response = await fetchWithDefaults('/users/api/profile/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors || 'Failed to save profile');
      }

      // Update the profile data directly instead of reloading to prevent flash
      setProfileData(prev => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          user: {
            ...prev.user,
            ...formData,
          },
        };
      });

      // Show success state (auto-cleared by useEffect)
      setSaveSuccess(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Error saving profile', error, { component: 'useProfile' });
      setError("We couldn't save your profile. Try again.");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [formData, setProfileData, setSaveSuccess]);

  const uploadAvatar = useCallback(
    async (file: File) => {
      try {
        setUploading(true);
        setUploadError(null);
        setUploadSuccess(null);

        const uploadFormData = new FormData();
        uploadFormData.append('avatar', file);

        const response = await fetchWithDefaults(
          '/users/api/profile/upload-image/',
          {
            method: 'POST',
            body: uploadFormData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to upload avatar');
        }

        const data = await response.json();

        // Update profile data with new avatar URL
        setProfileData(prev => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            user: {
              ...prev.user,
              avatar_url: data.avatar_url,
            },
          };
        });

        // Show success state (auto-cleared by useEffect)
        setUploadSuccess('Avatar uploaded successfully.');
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Error uploading avatar', error, {
          component: 'useProfile',
        });
        setUploadError("We couldn't upload your avatar. Try again.");
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [setProfileData]
  );

  /**
   * Factory function for creating profile field toggle handlers
   * @param fieldName - The field name in the profile data (e.g., 'email_footer_enabled')
   * @param setSaving - Saving state setter
   * @param setSaveSuccess - Success state setter
   * @param errorMessage - Error message prefix for logging
   * @returns Async toggle function with error handling and revert logic
   */
  const createProfileToggle = useCallback(
    (
      fieldName: keyof ProfileFormData,
      setSaving: (saving: boolean) => void,
      setSaveSuccess: ((success: boolean) => void) | undefined,
      errorMessage: string
    ) => {
      return async (enabled: boolean) => {
        try {
          setSaving(true);
          setSaveSuccess?.(false);
          setError(null);

          const updatedFormData = {
            ...formData,
            [fieldName]: enabled,
          };

          const response = await fetchWithDefaults('/users/api/profile/', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatedFormData),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.errors || 'Failed to save preference');
          }

          // Update both formData and profileData using functional updaters
          // to avoid overwriting concurrent user edits
          setFormData(prev => ({
            ...prev,
            [fieldName]: enabled,
          }));
          setProfileData(prev => {
            if (!prev) {
              return prev;
            }
            return {
              ...prev,
              user: {
                ...prev.user,
                [fieldName]: enabled,
              },
            };
          });

          // Show success state (auto-cleared by useEffect)
          setSaveSuccess?.(true);
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          logger.error(errorMessage, error, { component: 'useProfile' });
          setError(SAVE_PREFERENCE_ERROR);
          // Revert the toggle on error
          setFormData(prev => ({
            ...prev,
            [fieldName]: !enabled,
          }));
          setProfileData(prev => {
            if (!prev) {
              return prev;
            }
            return { ...prev, user: { ...prev.user, [fieldName]: !enabled } };
          });
          throw err;
        } finally {
          setSaving(false);
        }
      };
    },
    [formData, setProfileData]
  );

  /**
   * Factory function for creating membership notification toggle handlers
   * @param fieldName - The field name in the membership data (e.g., 'notify_on_mention')
   * @param setSaving - Saving state setter
   * @param setSaveSuccess - Success state setter
   * @param errorMessage - Error message prefix for logging
   * @returns Async toggle function with guard clauses and revert logic
   */
  const createMembershipToggle = useCallback(
    (
      fieldName:
        | 'notify_on_mention'
        | 'notify_on_owned_objects'
        | 'non_pipeline_actions'
        | 'capture_notes_without_calendar_meeting',
      setSaving: (saving: boolean) => void,
      setSaveSuccess: ((success: boolean) => void) | undefined,
      errorMessage: string,
      // Operation-scoped error setter. Defaults to the shared error so
      // existing toggles are unchanged; callers that render their own error
      // block pass a dedicated setter to avoid cross-operation leakage.
      setOperationError: (message: string | null) => void = setError
    ) => {
      return async (enabled: boolean) => {
        if (!orgId || !membershipId) {
          const error = new Error(
            'Cannot save notification preference: missing orgId or membershipId'
          );
          logger.error(errorMessage, error, { component: 'useProfile' });
          setOperationError(SAVE_PREFERENCE_ERROR);
          throw error;
        }

        try {
          setSaving(true);
          setSaveSuccess?.(false);
          setOperationError(null);

          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/memberships/${membershipId}/`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ [fieldName]: enabled }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to save preference');
          }

          setFormData(prev => ({
            ...prev,
            [fieldName]: enabled,
          }));

          // Update TanStack Query cache to prevent stale data on navigation
          setMembershipData(prev => {
            if (!prev) {
              return prev;
            }
            return { ...prev, [fieldName]: enabled };
          });

          // Show success state (auto-cleared by useEffect)
          setSaveSuccess?.(true);
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          logger.error(errorMessage, error, { component: 'useProfile' });
          setOperationError(SAVE_PREFERENCE_ERROR);
          // Revert the toggle on error
          setFormData(prev => ({
            ...prev,
            [fieldName]: !enabled,
          }));

          // Also revert cache to prevent stale data
          setMembershipData(prev => {
            if (!prev) {
              return prev;
            }
            return { ...prev, [fieldName]: !enabled };
          });

          throw err;
        } finally {
          setSaving(false);
        }
      };
    },
    [orgId, membershipId, setMembershipData]
  );

  const setEmailFooterEnabled = useCallback(
    async (enabled: boolean) => {
      await createProfileToggle(
        'email_footer_enabled',
        setEmailFooterSaving,
        undefined,
        'Error saving email footer preference'
      )(enabled);
      toast.success('Preference saved');
    },
    [createProfileToggle]
  );

  const setEmailSignatureEnabled = useCallback(
    async (enabled: boolean) => {
      await createProfileToggle(
        'email_signature_enabled',
        setEmailSignatureSaving,
        undefined,
        'Error saving email signature preference'
      )(enabled);
      toast.success('Preference saved');
    },
    [createProfileToggle]
  );

  // Persists the gmail.compose drafts opt-in. Callers are responsible for
  // triggering the Google re-consent round-trip when flipping this on — this
  // setter only owns the profile PATCH, matching the other profile toggles.
  const setGmailComposeDraftsEnabled = useCallback(
    async (enabled: boolean) => {
      await createProfileToggle(
        'gmail_compose_drafts_enabled',
        setGmailComposeDraftsSaving,
        undefined,
        'Error saving gmail compose drafts preference'
      )(enabled);
    },
    [createProfileToggle]
  );

  const setSuccessConfirmationEnabled = useCallback(
    async (enabled: boolean) => {
      await createProfileToggle(
        'show_proposal_success_state',
        setSuccessConfirmationSaving,
        undefined,
        'Error saving success confirmation preference'
      )(enabled);
      toast.success('Preference saved');
    },
    [createProfileToggle]
  );

  const setNotifyMentionEnabled = useCallback(
    async (enabled: boolean) => {
      await createMembershipToggle(
        'notify_on_mention',
        setNotifyMentionSaving,
        undefined,
        'Error saving mention notification preference'
      )(enabled);
      toast.success('Preference saved');
    },
    [createMembershipToggle]
  );

  const setNotifyOwnedEnabled = useCallback(
    async (enabled: boolean) => {
      await createMembershipToggle(
        'notify_on_owned_objects',
        setNotifyOwnedSaving,
        undefined,
        'Error saving owner notification preference'
      )(enabled);
      toast.success('Preference saved');
    },
    [createMembershipToggle]
  );

  const setNonPipelineActions = useCallback(
    async (enabled: boolean) => {
      await createMembershipToggle(
        'non_pipeline_actions',
        setNonPipelineActionsSaving,
        setNonPipelineActionsSaveSuccess,
        'Error saving pipeline-only preference',
        setNonPipelineActionsError
      )(enabled);
      // Success confirmation is a transient toast (matching saveAgentGuidance),
      // not an inline card. Only runs when the toggle above resolves — the
      // membership toggle rejects on error, so no toast fires on failure.
      toast.success('Preference saved');
    },
    [createMembershipToggle, setNonPipelineActionsSaveSuccess]
  );

  const setCaptureNotesWithoutCalendarMeeting = useCallback(
    async (enabled: boolean) => {
      await createMembershipToggle(
        'capture_notes_without_calendar_meeting',
        setCaptureNotesWithoutCalendarMeetingSaving,
        setCaptureNotesWithoutCalendarMeetingSaveSuccess,
        'Error saving capture-notes-without-calendar-meeting preference',
        setCaptureNotesWithoutCalendarMeetingError
      )(enabled);
      // Success confirmation is a transient toast (matching saveAgentGuidance),
      // not an inline card. Only runs when the toggle above resolves — the
      // membership toggle rejects on error, so no toast fires on failure.
      toast.success('Preference saved');
    },
    [createMembershipToggle, setCaptureNotesWithoutCalendarMeetingSaveSuccess]
  );

  const saveAgentGuidance = useCallback(async () => {
    if (!orgId || !membershipId) {
      const error = new Error(
        'Cannot save agent guidance: missing orgId or membershipId'
      );
      logger.error('Error saving agent guidance', error, {
        component: 'useProfile',
      });
      setError("We couldn't save your agent guidance. Try again.");
      throw error;
    }

    try {
      setAgentGuidanceSaving(true);
      setError(null);

      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/memberships/${membershipId}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ agent_guidance: formData.agent_guidance }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save agent guidance');
      }

      // Update local form state
      setFormData(prev => ({
        ...prev,
        agent_guidance: formData.agent_guidance,
      }));

      // Update TanStack Query cache to prevent stale data on navigation
      setMembershipData(prev => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          agent_guidance: formData.agent_guidance,
        };
      });

      // Log successful save for observability
      logger.info('Agent guidance saved successfully', {
        component: 'useProfile',
        orgId,
        membershipId,
      });

      toast.success('Agent guidance saved');
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Error saving agent guidance', error, {
        component: 'useProfile',
      });
      setError("We couldn't save your agent guidance. Try again.");

      // Revert formData to last known server state on error
      if (membershipData) {
        setFormData(prev => ({
          ...prev,
          agent_guidance: membershipData.agent_guidance ?? '',
        }));
      }

      throw err;
    } finally {
      setAgentGuidanceSaving(false);
    }
  }, [
    orgId,
    membershipId,
    formData.agent_guidance,
    membershipData,
    setMembershipData,
  ]);

  const setDigestCadence = useCallback(
    async (value: 'daily' | 'weekly' | 'none') => {
      if (!orgId || !membershipId) {
        const error = new Error(
          'Cannot save digest cadence: missing orgId or membershipId'
        );
        logger.error('Error saving digest cadence', error, {
          component: 'useProfile',
          orgId,
          membershipId,
        });
        setError("We couldn't save your digest cadence. Try again.");
        throw error;
      }

      const requestId = ++digestCadenceRequestRef.current;
      const previousValue = formData.digest_cadence;

      try {
        setDigestCadenceSaving(true);
        setError(null);

        // Optimistic update
        setFormData(prev => ({ ...prev, digest_cadence: value }));
        setMembershipData(prev =>
          prev ? { ...prev, digest_cadence: value } : prev
        );

        const response = await fetchWithDefaults(
          `/api/orgs/${orgId}/memberships/${membershipId}/`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ digest_cadence: value }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to save digest cadence');
        }

        if (requestId === digestCadenceRequestRef.current) {
          toast.success('Preference saved');
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Error saving digest cadence', error, {
          component: 'useProfile',
          orgId,
          membershipId,
        });

        // Only revert if this is still the latest request. A newer request
        // may have already succeeded with a different value; reverting here
        // would overwrite that correct value with a stale previousValue.
        if (requestId === digestCadenceRequestRef.current) {
          setMembershipData(prev =>
            prev ? { ...prev, digest_cadence: previousValue } : prev
          );
          setError("We couldn't save your digest cadence. Try again.");
          setFormData(prev => ({ ...prev, digest_cadence: previousValue }));
        }
        throw err;
      } finally {
        if (requestId === digestCadenceRequestRef.current) {
          setDigestCadenceSaving(false);
        }
      }
    },
    [orgId, membershipId, formData.digest_cadence, setMembershipData]
  );

  // setNotificationEmailAccount captures previousValue from formData before
  // the optimistic update so a failed PATCH reverts from this closure rather
  // than re-reading (possibly already-mutated) formData at error time.
  const setNotificationEmailAccount = useCallback(
    async (value: number | null) => {
      if (!orgId || !membershipId) {
        const error = new Error(
          'Cannot save notification email account: missing orgId or membershipId'
        );
        logger.error('Error saving notification email account', error, {
          component: 'useProfile',
          orgId,
          membershipId,
          attemptedValue: value,
        });
        setError(
          "We couldn't save your notification email selection. Try again."
        );
        throw error;
      }

      const requestId = ++notificationEmailRequestRef.current;
      const previousValue = formData.notification_social_account_id;

      try {
        setNotificationEmailSaving(true);
        // Clear any error from a prior failed save so a successful retry does
        // not leave the stale error banner up alongside the success state.
        setNotificationEmailError(null);
        setError(null);

        // Optimistic update
        setFormData(prev => ({
          ...prev,
          notification_social_account_id: value,
        }));
        setMembershipData(prev =>
          prev ? { ...prev, notification_social_account_id: value } : prev
        );

        const response = await fetchWithDefaults(
          `/api/orgs/${orgId}/memberships/${membershipId}/`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ notification_social_account_id: value }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail || 'Failed to save notification email account'
          );
        }

        if (requestId === notificationEmailRequestRef.current) {
          toast.success('Preference saved');
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Error saving notification email account', error, {
          component: 'useProfile',
          orgId,
          membershipId,
          attemptedValue: value,
          revertedValue: previousValue,
        });

        // Only revert if this is still the latest request. A newer request
        // may have already succeeded with a different value; reverting here
        // would overwrite that correct value with a stale previousValue.
        if (requestId === notificationEmailRequestRef.current) {
          setMembershipData(prev =>
            prev
              ? { ...prev, notification_social_account_id: previousValue }
              : prev
          );
          setFormData(prev => ({
            ...prev,
            notification_social_account_id: previousValue,
          }));
          setNotificationEmailError(
            "We couldn't save your notification email selection. Try again."
          );
        }
        throw err;
      } finally {
        if (requestId === notificationEmailRequestRef.current) {
          setNotificationEmailSaving(false);
        }
      }
    },
    [
      orgId,
      membershipId,
      formData.notification_social_account_id,
      setMembershipData,
    ]
  );

  // setBookingCalendarAccount mirrors setNotificationEmailAccount above: same
  // guard, optimistic update, and closure-captured revert on error.
  const setBookingCalendarAccount = useCallback(
    async (value: number | null) => {
      if (!orgId || !membershipId) {
        const error = new Error(
          'Cannot save booking calendar account: missing orgId or membershipId'
        );
        logger.error('Error saving booking calendar account', error, {
          component: 'useProfile',
          orgId,
          membershipId,
          attemptedValue: value,
        });
        setError(
          "We couldn't save your booking calendar preference. Try again."
        );
        throw error;
      }

      const requestId = ++bookingCalendarRequestRef.current;
      const previousValue = formData.booking_calendar_social_account_id;

      try {
        setBookingCalendarSaving(true);
        setBookingCalendarSaveSuccess(false);
        // Clear any error from a prior failed save so a successful retry does
        // not keep showing the stale failure banner.
        setBookingCalendarError(null);
        setError(null);

        // Optimistic update
        setFormData(prev => ({
          ...prev,
          booking_calendar_social_account_id: value,
        }));
        setMembershipData(prev =>
          prev ? { ...prev, booking_calendar_social_account_id: value } : prev
        );

        const response = await fetchWithDefaults(
          `/api/orgs/${orgId}/memberships/${membershipId}/`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ booking_calendar_social_account_id: value }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail || 'Failed to save booking calendar account'
          );
        }

        if (requestId === bookingCalendarRequestRef.current) {
          setBookingCalendarSaveSuccess(true);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Error saving booking calendar account', error, {
          component: 'useProfile',
          orgId,
          membershipId,
          attemptedValue: value,
          revertedValue: previousValue,
        });

        // Only revert if this is still the latest request. A newer request
        // may have already succeeded with a different value; reverting here
        // would overwrite that correct value with a stale previousValue.
        if (requestId === bookingCalendarRequestRef.current) {
          setMembershipData(prev =>
            prev
              ? { ...prev, booking_calendar_social_account_id: previousValue }
              : prev
          );
          setFormData(prev => ({
            ...prev,
            booking_calendar_social_account_id: previousValue,
          }));
          setBookingCalendarError(
            "We couldn't save your booking calendar preference. Try again."
          );
        }
        throw err;
      } finally {
        if (requestId === bookingCalendarRequestRef.current) {
          setBookingCalendarSaving(false);
        }
      }
    },
    [
      orgId,
      membershipId,
      formData.booking_calendar_social_account_id,
      setMembershipData,
      setBookingCalendarSaveSuccess,
    ]
  );

  const setTimeFormatPreference = useCallback(
    async (value: '24h' | '12h' | '') => {
      if (!orgId || !membershipId) {
        const error = new Error(
          'Cannot save time format: missing orgId or membershipId'
        );
        logger.error('Error saving time format', error, {
          component: 'useProfile',
          orgId,
          membershipId,
        });
        setError("We couldn't save your time format. Try again.");
        throw error;
      }

      const requestId = ++timeFormatRequestRef.current;
      const previousValue = membershipData?.time_format ?? '';

      try {
        setTimeFormatSaving(true);
        setError(null);

        // Optimistic update — membership cache first, then the org-context
        // cache so useOrgTimeFormat consumers app-wide pick it up instantly.
        setMembershipData(prev =>
          prev ? { ...prev, time_format: value } : prev
        );
        if (currentOrg?.id === orgId && currentOrg.currentUserMembership) {
          updateOrgFields(orgId, {
            currentUserMembership: {
              ...currentOrg.currentUserMembership,
              timeFormat: value,
            },
          });
        }

        const response = await fetchWithDefaults(
          `/api/orgs/${orgId}/memberships/${membershipId}/`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ time_format: value }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to save time format');
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Error saving time format', error, {
          component: 'useProfile',
          orgId,
          membershipId,
        });

        // Only revert if this is still the latest request (a newer request may
        // have already succeeded with a different value).
        if (requestId === timeFormatRequestRef.current) {
          setMembershipData(prev =>
            prev ? { ...prev, time_format: previousValue } : prev
          );
          if (currentOrg?.id === orgId && currentOrg.currentUserMembership) {
            updateOrgFields(orgId, {
              currentUserMembership: {
                ...currentOrg.currentUserMembership,
                timeFormat: previousValue,
              },
            });
          }
          setError("We couldn't save your time format. Try again.");
        }
        throw err;
      } finally {
        if (requestId === timeFormatRequestRef.current) {
          setTimeFormatSaving(false);
        }
      }
    },
    [
      orgId,
      membershipId,
      membershipData?.time_format,
      setMembershipData,
      currentOrg,
      updateOrgFields,
    ]
  );

  const setNotificationChannel = useCallback(
    async (
      type: NotificationType,
      channel: NotificationChannel,
      enabled: boolean
    ) => {
      if (!orgId || !membershipId) {
        const err = new Error(
          'Cannot save notification channels: missing orgId or membershipId'
        );
        logger.error('Error saving notification channels', err, {
          component: 'useProfile',
          orgId,
          membershipId,
        });
        setError("We couldn't save your notification channels. Try again.");
        throw err;
      }

      const key = `${type}:${channel}`;
      notificationChannelRefs.current[key] =
        (notificationChannelRefs.current[key] ?? 0) + 1;
      const requestId = notificationChannelRefs.current[key];

      // Read current channels synchronously via ref so newDict is available
      // before the first await (fetchWithDefaults). Using a ref avoids adding
      // formData.notification_channels to the useCallback dep array.
      const previousChannels = notificationChannelsRef.current;
      const current =
        previousChannels[type] ?? DEFAULT_NOTIFICATION_CHANNELS[type] ?? [];
      const next = enabled
        ? Array.from(new Set([...current, channel]))
        : current.filter(c => c !== channel);
      const newDict = { ...previousChannels, [type]: next };

      try {
        setNotificationChannelsSaving(true);
        setError(null);

        setFormData(prev => ({ ...prev, notification_channels: newDict }));
        setMembershipData(prev =>
          prev ? { ...prev, notification_channels: newDict } : prev
        );

        const response = await fetchWithDefaults(
          `/api/orgs/${orgId}/memberships/${membershipId}/`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ notification_channels: newDict }),
          }
        );

        if (!response.ok) {
          const errorData = (await response.json()) as Record<string, unknown>;
          const channels = errorData.notification_channels;
          const msg =
            (Array.isArray(channels) ? channels[0] : undefined) ??
            errorData.detail ??
            'Failed to save preference';
          throw new Error(String(msg));
        }

        if (requestId === notificationChannelRefs.current[key]) {
          const data = (await response.json()) as {
            notification_channels?: NotificationChannels;
          };
          const serverChannels = data.notification_channels;
          setFormData(prev => {
            const merged = serverChannels
              ? {
                  ...serverChannels,
                  ...prev.notification_channels,
                  [type]: serverChannels[type],
                }
              : prev.notification_channels;
            return { ...prev, notification_channels: merged };
          });
          setMembershipData(prev => {
            if (!prev) {
              return prev;
            }
            const merged = serverChannels
              ? {
                  ...serverChannels,
                  ...prev.notification_channels,
                  [type]: serverChannels[type],
                }
              : prev.notification_channels;
            return { ...prev, notification_channels: merged };
          });
          toast.success('Preference saved');
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Error saving notification channels', error, {
          component: 'useProfile',
          orgId,
          membershipId,
          notificationType: type,
          channel,
        });

        if (requestId === notificationChannelRefs.current[key]) {
          setFormData(prev => ({
            ...prev,
            notification_channels: {
              ...prev.notification_channels,
              [type]: previousChannels[type],
            },
          }));
          setMembershipData(prev =>
            prev
              ? {
                  ...prev,
                  notification_channels: {
                    ...prev.notification_channels,
                    [type]: previousChannels[type],
                  },
                }
              : prev
          );
          setError("We couldn't save your notification channels. Try again.");
        }
        throw err;
      } finally {
        if (requestId === notificationChannelRefs.current[key]) {
          setNotificationChannelsSaving(false);
        }
      }
    },
    [orgId, membershipId, setMembershipData]
  );

  // The retired v1 auto_approve_creation setting has no save path here any
  // more: the ratified agent settings live on the ahoy_agent entity
  // (useAgentSettings), and the membership field is closed to API writes —
  // its stored historical values only feed backfill_agents' one-way legacy
  // migration (docs/claude/AGENT_AUTONOMY.md).

  return {
    // Data
    profileData,
    profileConfirmed,
    formData,
    membershipId,

    // Loading states
    loading,
    membershipLoading,
    saving,
    uploading,
    emailFooterSaving,
    emailSignatureSaving,
    gmailComposeDraftsSaving,
    successConfirmationSaving,
    notifyMentionSaving,
    notifyOwnedSaving,
    nonPipelineActionsSaving,
    captureNotesWithoutCalendarMeetingSaving,
    agentGuidanceSaving,
    digestCadenceSaving,
    timeFormatSaving,
    notificationChannelsSaving,
    notificationEmailSaving,
    bookingCalendarSaving,

    // Success states
    saveSuccess,
    uploadSuccess,
    nonPipelineActionsSaveSuccess,
    captureNotesWithoutCalendarMeetingSaveSuccess,
    bookingCalendarSaveSuccess,

    // Error states
    error:
      error ??
      (queryError ? "We couldn't load your profile. Try again." : null),
    uploadError,
    membershipLoadError,
    nonPipelineActionsError,
    notificationEmailError,
    bookingCalendarError,
    captureNotesWithoutCalendarMeetingError,

    // Actions
    loadProfileData,
    updateFormData,
    saveProfile,
    uploadAvatar,
    setEmailFooterEnabled,
    setEmailSignatureEnabled,
    setGmailComposeDraftsEnabled,
    setSuccessConfirmationEnabled,
    setNotifyMentionEnabled,
    setNotifyOwnedEnabled,
    setNonPipelineActions,
    setCaptureNotesWithoutCalendarMeeting,
    saveAgentGuidance,
    setDigestCadence,
    setNotificationEmailAccount,
    setBookingCalendarAccount,
    setTimeFormatPreference,
    setNotificationChannel,
    // Current membership time-format preference ('' = org default)
    timeFormatPreference: membershipData?.time_format ?? '',
  };
}
